import { getPresentationHandlers } from './presentationHandlers';
import type { ActivationBatch } from './state/activationProgressSlice';

/**
 * Items the user submitted in the activation: the devices the vendor batch polls
 * plus the billing-only re-enrollments, which settle with the order itself and
 * so never appear in `total`.
 */
export const getBatchSelectionTotal = (batch: ActivationBatch): number =>
  batch.total + (batch.reenrollmentCount ?? 0);

/**
 * Whether the batch reports through the stacked progress/completion card rather
 * than a toast. Keyed off the whole submitted selection (see
 * {@link getBatchSelectionTotal}) so a mixed activation — say one new device
 * alongside two re-enrollments — is summarized as the bulk action the user
 * performed instead of collapsing to a single-device toast that would drop the
 * re-enrollments. Also always true once a batch carries an explicit
 * `presentationGroup`: a customized integration's card must never silently
 * downgrade to the generic single-device toast — e.g. a retry that resubmits
 * just one of several originally-failed items would otherwise drop its
 * `presentationGroup`'s title/text/action along with the card itself. Shared
 * by the snackbar, the poller's terminal handling, and the in-progress card so
 * they can't disagree on what counts as bulk.
 */
export const isBulkBatch = (batch: ActivationBatch): boolean =>
  getBatchSelectionTotal(batch) > 1 || batch.presentationGroup != null;

/**
 * Reserved key for batches that omit `presentationGroup` (the default
 * peripheral activation flow). Deliberately not a plausible value for a real
 * caller to pick — an integration using this exact string as its own
 * `presentationGroup` would merge its batches into the default peripheral
 * card, and vice versa.
 */
export const DEFAULT_PRESENTATION_GROUP = '__default_peripheral_group__';

/**
 * Which card a batch belongs to. Batches from different integrations (or an
 * integration and the default peripheral flow) must never share a card —
 * otherwise one batch's title/text/action would be misapplied to another's
 * rows, and their success/failure counts would be summed together.
 */
export const getPresentationGroupKey = (batch: ActivationBatch): string =>
  batch.presentationGroup ?? DEFAULT_PRESENTATION_GROUP;

/**
 * Splits batches into presentation groups (see {@link getPresentationGroupKey}),
 * preserving the order each group first appeared in so stacked cards don't
 * reorder as batches change status.
 */
export const groupBatchesByPresentation = (
  batches: ActivationBatch[]
): ActivationBatch[][] => {
  const groups = new Map<string, ActivationBatch[]>();
  for (const batch of batches) {
    const key = getPresentationGroupKey(batch);
    const group = groups.get(key);
    if (group) {
      group.push(batch);
    } else {
      groups.set(key, [batch]);
    }
  }
  return [...groups.values()];
};

export type CompletionSummary = {
  successCount: number;
  failureCount: number;
  reenrollmentSuccessCount: number;
  reenrollmentFailureCount: number;
  enrolledVehicleCount: number;
  productName?: string;
  completeTitle?: string;
  activatedText?: (count: number) => string;
  failedText?: (count: number) => string;
  primaryActionLabel?: string;
  onPrimaryAction?: () => void;
};

/**
 * Aggregates a single presentation group's terminal batches into the
 * completion card's props. Safe to pick overrides via first-match here —
 * unlike aggregating across groups, every batch passed in shares the same
 * `presentationGroup` by construction (see {@link groupBatchesByPresentation}), so they
 * can't disagree.
 */
export const summarizeCompletedBatches = (
  batches: ActivationBatch[]
): CompletionSummary => {
  const handlers = batches[0]
    ? getPresentationHandlers(getPresentationGroupKey(batches[0]))
    : undefined;
  const primaryAction = batches.find((batch) => batch.primaryActionLabel);

  return {
    successCount: batches.reduce((sum, batch) => sum + batch.success, 0),
    failureCount: batches.reduce((sum, batch) => sum + batch.failure, 0),
    // Re-enrollments settle on the billing side, so a batch's re-enrolled
    // devices all share the order's outcome — split them into the
    // success/failure rows by that single flag rather than by per-device
    // counts.
    reenrollmentSuccessCount: batches.reduce(
      (sum, batch) =>
        sum + (batch.reenrollmentFailed ? 0 : (batch.reenrollmentCount ?? 0)),
      0
    ),
    reenrollmentFailureCount: batches.reduce(
      (sum, batch) =>
        sum + (batch.reenrollmentFailed ? (batch.reenrollmentCount ?? 0) : 0),
      0
    ),
    // Vehicles enrolled alongside these batches (OST mixed enroll) so the card
    // reports enroll + activation as one summary. 0 for every other flow.
    enrolledVehicleCount: batches.reduce(
      (sum, batch) => sum + (batch.enrolledVehicleCount ?? 0),
      0
    ),
    productName: batches.find((batch) => batch.productName)?.productName,
    completeTitle: batches.find((batch) => batch.completeTitle)?.completeTitle,
    activatedText: handlers?.activatedText,
    failedText: handlers?.failedText,
    primaryActionLabel: primaryAction?.primaryActionLabel,
    onPrimaryAction: handlers?.onPrimaryAction,
  };
};
